// src/components/Layout/Footer/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaFacebook, 
  FaInstagram, 
  FaWhatsapp,
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock
} from 'react-icons/fa';
import { trackBusinessEvents } from '../../../utils/analytics';
import styles from './Footer.module.css';

const Footer = () => {
  const { t } = useTranslation(['common']);
  const currentYear = new Date().getFullYear();

  const handleSocialClick = (platform) => {
    const urls = {
      facebook: process.env.REACT_APP_FACEBOOK_URL || 'https://facebook.com/adtac.co',
      instagram: process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/adtac.co',
      whatsapp: `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '60198008013'}?text=${encodeURIComponent(
        t('footer.whatsappMessage')
      )}`,
      linkedin: process.env.REACT_APP_LINKEDIN_URL || 'https://www.linkedin.com/company/ad-training-consultancy-adtac/',
    };
    
    // Track social media clicks
    trackBusinessEvents.socialMediaClick(platform, 'footer');
    window.open(urls[platform], '_blank', 'noopener,noreferrer');
  };

  const handleCallClick = (phoneNumber) => {
    trackBusinessEvents.phoneClick(phoneNumber, 'footer');
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmailClick = () => {
    const subject = encodeURIComponent(t('footer.emailSubject'));
    const body = encodeURIComponent(t('footer.emailBody'));
    
    trackBusinessEvents.emailClick('info@adtac.co', 'footer');
    window.location.href = `mailto:info@adtac.co?subject=${subject}&body=${body}`;
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContent}>
        <div className={styles.container}>
          <div className={styles.footerGrid}>
            
            {/* Brand Section - Column 1 */}
            <div className={`${styles.footerSection} ${styles.brandSection}`}>
              <img 
                src="/assets/logo/HeaderFooter_logo.png" 
                alt="ADTAC - knowledge resolution" 
                className={styles.footerLogo}
              />
              <p className={styles.companyDescription}>
                {t('footer.companyDescription')}
                <br />
                <br />
                {t('footer.tagline')}
              </p>
              
              {/* Social Media Section */}
              <div className={styles.socialSection}>
                <h4 className={styles.socialTitle}>
                  {t('footer.followUs')}
                </h4>
                <div className={styles.socialIcons}>
                  <button 
                    className={styles.socialIcon}
                    onClick={() => handleSocialClick('facebook')}
                    aria-label={t('footer.facebookLabel')}
                  >
                    <FaFacebook />
                  </button>
                  <button 
                    className={styles.socialIcon}
                    onClick={() => handleSocialClick('instagram')}
                    aria-label={t('footer.instagramLabel')}
                  >
                    <FaInstagram />
                  </button>
                  <button 
                    className={styles.socialIcon}
                    onClick={() => handleSocialClick('linkedin')}
                    aria-label={t('footer.linkedinLabel')}
                  >
                    <FaLinkedinIn />
                  </button>
                  <button 
                    className={styles.socialIcon}
                    onClick={() => handleSocialClick('whatsapp')}
                    aria-label={t('footer.whatsappLabel')}
                  >
                    <FaWhatsapp />
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Links Section - Column 2 */}
            <div className={`${styles.footerSection} ${styles.linksSection}`}>
              <h3 className={styles.sectionTitle}>
                {t('footer.quickLinks')}
              </h3>
              <ul className={styles.linksList}>
                <li>
                  <Link to="/consultancy-services">
                    {t('footer.services')}
                  </Link>
                </li>
                <li>
                  <Link to="/management-system-training">
                    {t('footer.training')}
                  </Link>
                </li>
                <li>
                  <Link to="/gallery">
                    {t('footer.gallery')}
                  </Link>
                </li>
                <li>
                  <Link to="/blog">
                    {t('footer.blog')}
                  </Link>
                </li>
                <li>
                  <Link to="/about-us">
                    {t('footer.aboutUs')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact">
                    {t('footer.contact')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Section - Column 3 */}
            <div className={`${styles.footerSection} ${styles.contactSection}`}>
              <h3 className={styles.sectionTitle}>
                {t('footer.getInTouch')}
              </h3>
              
              <div className={styles.contactInfo}>
                <div className={styles.contactItem}>
                  <FaPhone className={styles.contactIcon} />
                  <div className={styles.contactText}>
                    <a 
                      href="tel:+60132228584"
                      onClick={() => handleCallClick('+60132228584')}
                    >
                      013 222 8584
                    </a> / 
                    <a 
                      href="tel:+60198008013"
                      onClick={() => handleCallClick('+60198008013')}
                    >
                      019 800 8013
                    </a>
                  </div>
                </div>
                
                <div className={styles.contactItem}>
                  <FaEnvelope className={styles.contactIcon} />
                  <div className={styles.contactText}>
                    <a href="mailto:info@adtac.co" onClick={handleEmailClick}>
                      info@adtac.co
                    </a>
                  </div>
                </div>
                
                <div className={styles.contactItem}>
                  <FaClock className={styles.contactIcon} />
                  <div className={styles.contactText}>
                    {t('footer.workingHours').split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index === 0 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className={styles.contactItem}>
                  <FaMapMarkerAlt className={styles.contactIcon} />
                  <div className={styles.contactText}>
                    {t('footer.address').split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index === 0 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className={styles.footerBottom}>
        <div className={styles.container}>
          <div className={styles.footerBottomContent}>
            <p className={styles.copyright}>
              {t('footer.copyright', { year: currentYear })}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;